import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { categoryLabel } from "../lib/categories";
import { getSiteMeta } from "../lib/meta";

const LINES = ["STYLE.", "SOUND.", "SPEED.", "FUTURE."];

const scrollToLatest = () => {
  if (window.__lenis) window.__lenis.scrollTo("#latest", { offset: -110 });
  else document.getElementById("latest")?.scrollIntoView({ behavior: "smooth" });
};

export default function Hero({ featured, freshCount = 0 }) {
  const ref = useRef(null);
  const [issue, setIssue] = useState(1);

  useEffect(() => {
    getSiteMeta().then((m) => setIssue(m.issue));
  }, []);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section data-testid="hero-section" ref={ref} onMouseMove={onMove}
      className="relative min-h-[88vh] flex items-center overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[540px] h-[540px] rounded-full bg-cobalt/20 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[480px] h-[480px] rounded-full bg-kinetic/10 blur-[140px] pointer-events-none" />

      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center w-full relative">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-4">
            Issue Nº {String(issue).padStart(3, "0")} — The Chronicle of Modern Man
          </motion.p>

          {freshCount > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.6 }}
              data-testid="freshness-badge"
              className="inline-flex items-center gap-2.5 border border-kinetic/40 bg-kinetic/5 rounded-full px-4 py-2 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-kinetic opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-kinetic" />
              </span>
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-kinetic">
                Updated today · {freshCount} new {freshCount === 1 ? "story" : "stories"}
              </span>
            </motion.div>
          )}

          <h1 data-testid="hero-headline" className="font-display font-black text-[12.5vw] sm:text-7xl lg:text-8xl leading-[0.92] tracking-tight uppercase">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.13, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className={`block ${i === LINES.length - 1 ? "text-outline-lime" : ""}`}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 text-lg text-ash max-w-xl leading-relaxed">
            Fashion, music, machines, ink and ideas — a magazine for men who move first,
            and a community where every reader has a voice.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-cta-button" onClick={scrollToLatest}
              className="group bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium px-7 py-4 rounded-full flex items-center gap-2 hover:bg-porcelain transition-colors duration-300">
              Read the latest <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <Link to="/voices" data-testid="hero-voices-cta"
              className="group border border-white/15 hover:border-kinetic/60 text-porcelain font-mono text-xs uppercase tracking-[0.18em] px-7 py-4 rounded-full flex items-center gap-2 transition-colors duration-300">
              Join the voices <ArrowUpRight size={15} className="text-kinetic group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-5" style={{ perspective: 1200 }}>
          {featured && (
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
              <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
                <Link to={`/article/${featured.slug}`} data-testid="featured-article-card-hero"
                  className="block group relative border border-white/10 bg-surface overflow-hidden">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={featured.image_url} alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6" style={{ transform: "translateZ(40px)" }}>
                    <span className="inline-block bg-kinetic text-obsidian text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1 mb-3">
                      {categoryLabel(featured.category)} — Featured
                    </span>
                    <h3 className="font-display font-bold text-2xl leading-tight">{featured.title}</h3>
                    <p className="text-ash text-xs font-mono mt-3 uppercase tracking-[0.15em]">
                      {featured.author_name} · {featured.read_minutes} min read
                    </p>
                  </div>
                </Link>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
